import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    ClickAwayListener,
    Divider,
    Drawer,
    IconButton,
    Paper,
    Popper,
    Stack,
    Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { API_BASE_URL, fetchJson } from './api';

const NON_CONSTRUCTION_REFUSAL = 'I apologise, I am unable to answer that. I am a construction helpdesk tool only!';

const ADMIN_ONLY_SETTING_MESSAGE = "Apologies, this setting is for Organisation Admins only, please contact your line manager for information.";

const ADMIN_ONLY_SETTINGS_SECTIONS = new Set([
    'smtp',
    'org_profile',
    'retention',
    'integrations_admin',
    'security_policy',
    'ai_policy',
    'email_templates',
    'analytics_export',
    'issue_reporting',
    'sla',
    'finance'
]);

const ALLOWED_NAV_VIEWS = new Set([
    'overview',
    'reports',
    'activity',
    'request_valuation',
    'calendar',
    'projects',
    'financials',
    'analytics',
    'team',
    'clients',
    'settings',
    'issue_reports'
]);

function firstNameFromDisplayName(displayName) {
    const parts = String(displayName || '').trim().split(/\s+/).filter(Boolean);
    return parts[0] || 'there';
}

function normaliseRole(raw) {
    const r = String(raw || '').trim().toLowerCase();
    if (!r) return '';
    if (r === 'admin') return 'admin';
    if (r === 'surveyor' || r === 'standard') return 'surveyor';
    if (r === 'developer') return 'developer';
    if (r === 'funder' || r === 'finance') return 'funder';
    return r;
}

function isFlagrantlyNonConstruction(text) {
    const t = String(text || '').trim().toLowerCase();
    if (!t) return false;

    // Strong non-construction signals.
    const banned = [
        'celebrity', 'movie', 'film', 'tv', 'football', 'soccer', 'basketball', 'tennis', 'cricket',
        'election', 'politics', 'religion', 'dating', 'relationship', 'girlfriend', 'boyfriend',
        'astrology', 'horoscope', 'stock tips', 'crypto', 'gambling', 'casino', 'lottery',
        'recipe', 'cook', 'diet', 'workout', 'gym plan',
        'joke', 'meme'
    ];

    // Construction/statutory signals.
    const allowed = [
        'construction', 'project', 'site', 'works', 'programme', 'program', 'contractor', 'subcontractor',
        'valuation', 'interim', 'certificate', 'drawdown', 'cost', 'budget', 'variation',
        'planning', 'statutory', 'building regs', 'building regulations', 'nhbc', 'warranty',
        'cdm', 'health and safety', 'h&s', 'fire', 'cladding',
        'schedule', 'milestone', 'procurement', 'design', 'riba',
        'funder', 'borrower', 'consultant', 'architect', 'engineer', 'qs',
        'conditions', 'notice', 'section 106', 's106', 'bcis'
    ];

    const hasAllowed = allowed.some((k) => t.includes(k));
    if (hasAllowed) return false;

    const hasBanned = banned.some((k) => t.includes(k));
    if (hasBanned) return true;
    return false;
}

function extractNavigationIntent(message) {
    const t = String(message || '').trim().toLowerCase();
    if (!t) return null;

    const isCommandy = /\b(open|go to|goto|navigate|take me|show|request|where|how do i|how can i)\b/.test(t)
        || /\b(settings|projects|project|reports|calendar|analytics|team|workload|financials|overview|valuation|interim|sla|service level)\b/.test(t);
    if (!isCommandy) return null;

    // Settings sections
    if (t.includes('sla') || t.includes('service level agreement')) return { view: 'settings', section: 'sla', label: 'Jump to SLA settings' };
    if (t.includes('issue reporting') || t.includes('issue report settings')) return { view: 'settings', section: 'issue_reporting', label: 'Jump to Issue Reporting settings' };
    if (t.includes('smtp') || t.includes('mail server')) return { view: 'settings', section: 'smtp', label: 'Jump to SMTP settings' };
    if (t.includes('retention')) return { view: 'settings', section: 'retention', label: 'Jump to Retention settings' };
    if (t.includes('security policy')) return { view: 'settings', section: 'security_policy', label: 'Jump to Security Policy settings' };
    if (t.includes('ai policy')) return { view: 'settings', section: 'ai_policy', label: 'Jump to AI Policy settings' };
    if (t.includes('mfa') || t.includes('two factor') || t.includes('2fa')) return { view: 'settings', section: 'mfa', label: 'Jump to MFA settings' };

    // Views
    if (t.includes('settings') || t.includes('password')) return { view: 'settings', label: 'Jump to Settings' };
    if (t.includes('request valuation') || t.includes('valuation request') || t.includes('interim questionnaire')) return { view: 'request_valuation', label: 'Jump to Request Valuation' };
    if (t.includes('calendar') || t.includes('diary')) return { view: 'calendar', label: 'Jump to Calendar' };
    if (t.includes('analytics') || t.includes('usage') || t.includes('kpi')) return { view: 'analytics', label: 'Jump to Analytics' };
    if (t.includes('team') || t.includes('users') || t.includes('member')) return { view: 'team', label: 'Jump to Team' };
    if (t.includes('workload') || t.includes('capacity')) return { view: 'analytics', tab: 'workload', label: 'Jump to Workload' };
    if (t.includes('finance') || t.includes('financial')) return { view: 'financials', label: 'Jump to Financials' };
    if (t.includes('project') || t.includes('projects') || t.includes('reports') || t.includes('report')) return { view: 'reports', label: 'Jump to Projects' };
    if (t.includes('overview') || t.includes('dashboard') || t === 'home') return { view: 'overview', label: 'Jump to Overview' };
    return null;
}

function parseNavigateDirective(text) {
    const raw = String(text || '');
    const lines = raw.split(/\r?\n/);
    const idx = lines.findIndex((l) => /^\s*NAVIGATE\b/i.test(String(l || '')));
    if (idx < 0) return { cleanText: raw.trim(), nav: null };

    const line = String(lines[idx] || '').trim();
    let nav = null;

    // JSON form: NAVIGATE {"view":"settings","section":"sla","label":"Open SLA settings"}
    const jsonPart = line.replace(/^NAVIGATE\s*/i, '').trim();
    if (jsonPart.startsWith('{') && jsonPart.endsWith('}')) {
        try {
            const obj = JSON.parse(jsonPart);
            let view = String(obj?.view || '').trim();
            const section = String(obj?.section || '').trim();
            const label = String(obj?.label || '').trim();
            const tab = String(obj?.tab || '').trim();

            // Back-compat: accept NAVIGATE view=workload but route via analytics tab.
            if (String(view).trim().toLowerCase() === 'workload') {
                view = 'analytics';
            }

            if (view && ALLOWED_NAV_VIEWS.has(view)) {
                const effectiveTab = String(obj?.view || '').trim().toLowerCase() === 'workload' ? 'workload' : tab;
                nav = { view, ...(section ? { section } : {}), ...(effectiveTab ? { tab: effectiveTab } : {}), ...(label ? { label } : {}) };
            }
        } catch {
            nav = null;
        }
    }

    // Key/value form: NAVIGATE view=settings section=sla label="Open SLA settings"
    if (!nav) {
        const viewMatch = line.match(/\bview\s*=\s*([a-z_]+)\b/i);
        const tabMatch = line.match(/\btab\s*=\s*([a-z0-9_-]+)\b/i);
        const sectionMatch = line.match(/\bsection\s*=\s*([a-z0-9_-]+)\b/i);
        const labelMatch = line.match(/\blabel\s*=\s*"([^"]+)"|\blabel\s*=\s*'([^']+)'/i);

        let view = viewMatch ? String(viewMatch[1] || '').trim() : '';
        const tab = tabMatch ? String(tabMatch[1] || '').trim() : '';
        const section = sectionMatch ? String(sectionMatch[1] || '').trim() : '';
        const label = labelMatch ? String(labelMatch[1] || labelMatch[2] || '').trim() : '';

        // Back-compat: accept NAVIGATE view=workload but route via analytics tab.
        const isWorkload = String(view).trim().toLowerCase() === 'workload';
        if (isWorkload) view = 'analytics';

        if (view && ALLOWED_NAV_VIEWS.has(view)) {
            const effectiveTab = isWorkload ? 'workload' : tab;
            nav = { view, ...(section ? { section } : {}), ...(effectiveTab ? { tab: effectiveTab } : {}), ...(label ? { label } : {}) };
        }
    }

    const cleanLines = lines.filter((_, i) => i !== idx);
    const cleanText = cleanLines.join('\n').trim();
    return { cleanText, nav };
}

function isAdminOnlySettingsNav(nav) {
    const view = String(nav?.view || '').trim();
    if (view !== 'settings') return false;
    const section = String(nav?.section || '').trim();
    return Boolean(section && ADMIN_ONLY_SETTINGS_SECTIONS.has(section));
}

function getAuthToken() {
    try {
        return String(localStorage.getItem('monsurv_auth_token') || '').trim();
    } catch {
        return '';
    }
}

export default function ChatAssistantDrawer({
    open,
    onClose,
    anchorEl,
    displayName,
    role,
    portalMode,
    projectReference,
    reportInstance,
    onNavigate
}) {
    const normRole = useMemo(() => normaliseRole(role || portalMode), [role, portalMode]);
    const canUseSurveyorAssistant = normRole === 'surveyor' || normRole === 'admin';

    const [messages, setMessages] = useState(() => []);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const scrollRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        setError('');
        if (!messages.length) {
            const first = firstNameFromDisplayName(displayName);
            setMessages([{ role: 'assistant', content: `Hi ${first}, what can I help you with today?` }]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        try {
            const el = scrollRef.current;
            if (el) el.scrollTop = el.scrollHeight;
        } catch {
            // ignore
        }
    }, [messages.length, open]);

    async function send() {
        const q = String(input || '').trim();
        if (!q || busy) return;

        setError('');
        setBusy(true);
        setInput('');

        setMessages((prev) => [...prev, { role: 'user', content: q }]);

        try {
            if (isFlagrantlyNonConstruction(q)) {
                setMessages((prev) => [...prev, { role: 'assistant', content: NON_CONSTRUCTION_REFUSAL }]);
                return;
            }

            // Natural-language navigation (no tags/buttons).
            const navIntent = extractNavigationIntent(q);
            if (navIntent && onNavigate) {
                if (normRole !== 'admin' && isAdminOnlySettingsNav(navIntent)) {
                    setMessages((prev) => [...prev, { role: 'assistant', content: ADMIN_ONLY_SETTING_MESSAGE }]);
                    return;
                }

                const label = String(navIntent?.label || 'Jump to');
                const where = navIntent.section
                    ? `Settings → ${String(navIntent.section).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}`
                    : String(navIntent.view || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

                const polite =
                    `You can do that from ${where}.\n\n` +
                    `If you'd like, click “${label}” and I’ll take you there.`;

                setMessages((prev) => [...prev, { role: 'assistant', content: polite, nav: navIntent }]);
                return;
            }

            // Surveyor/admin get project copilot when a project is in context.
            if (canUseSurveyorAssistant && projectReference) {
                const ri = reportInstance || 'initial';
                const url = `${API_BASE_URL}/project/${encodeURIComponent(projectReference)}/copilot/chat?mode=surveyor&report_instance=${encodeURIComponent(ri)}`;

                const next = (prev) => {
                    const list = Array.isArray(prev) ? prev : [];
                    // Send only the last N messages and keep it light.
                    return list
                        .filter((m) => m && typeof m === 'object')
                        .map((m) => ({
                            role: String(m.role || '').toLowerCase() === 'assistant' ? 'assistant' : 'user',
                            content: String(m.content || '').trim()
                        }))
                        .filter((m) => m.content)
                        .slice(-12);
                };

                const token = getAuthToken();
                const json = await fetchJson(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {})
                    },
                    body: JSON.stringify({ messages: next([...messages, { role: 'user', content: q }]) })
                });

                const text = String(json?.reply?.text || json?.text || '').trim();
                const parsed = parseNavigateDirective(text);
                if (parsed?.nav && normRole !== 'admin' && isAdminOnlySettingsNav(parsed.nav)) {
                    setMessages((prev) => [...prev, { role: 'assistant', content: ADMIN_ONLY_SETTING_MESSAGE }]);
                    return;
                }
                setMessages((prev) => [...prev, { role: 'assistant', content: parsed.cleanText || 'No response returned.', nav: parsed.nav }]);
                return;
            }

            // General MonsurvAI construction chatbot (all roles).
            const url = `${API_BASE_URL}/ai/chat`;
            const token = getAuthToken();

            const toHistory = (prev) => {
                const list = Array.isArray(prev) ? prev : [];
                return list
                    .filter((m) => m && typeof m === 'object')
                    .map((m) => ({
                        role: String(m.role || '').toLowerCase() === 'assistant' ? 'assistant' : 'user',
                        content: String(m.content || '').trim()
                    }))
                    .filter((m) => m.content)
                    .slice(-12);
            };

            const json = await fetchJson(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    messages: toHistory([...messages, { role: 'user', content: q }]),
                    context: {
                        role: normRole,
                        project_reference: projectReference || null,
                        report_instance: reportInstance || null
                    }
                })
            });

            const text = String(json?.reply?.text || json?.text || '').trim();
            const parsed = parseNavigateDirective(text);
            if (parsed?.nav && normRole !== 'admin' && isAdminOnlySettingsNav(parsed.nav)) {
                setMessages((prev) => [...prev, { role: 'assistant', content: ADMIN_ONLY_SETTING_MESSAGE }]);
                return;
            }
            setMessages((prev) => [...prev, { role: 'assistant', content: parsed.cleanText || 'No response returned.', nav: parsed.nav }]);
        } catch (e) {
            setError(e?.message || 'Chat request failed');
            setMessages((prev) => [...prev, { role: 'assistant', content: 'Sorry — I hit an error. Please try again.' }]);
        } finally {
            setBusy(false);
        }
    }


    const content = (
        <Stack sx={{ height: '100%' }}>
            <Box
                sx={{
                    px: 2.25,
                    py: 1.75,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.96) 0%, rgba(30, 64, 175, 0.9) 54%, rgba(15, 118, 110, 0.78) 100%)',
                    color: '#f8fafc'
                }}
            >
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2.5,
                            display: 'grid',
                            placeItems: 'center',
                            background: 'linear-gradient(145deg, rgba(255,255,255,0.22) 0%, rgba(191,219,254,0.18) 100%)',
                            border: '1px solid rgba(255,255,255,0.18)',
                            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.14)'
                        }}
                    >
                        <SmartToyIcon sx={{ fontSize: 22 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 900, letterSpacing: '-0.01em' }}>MonsurvAI</Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(248, 250, 252, 0.8)' }}>
                            {canUseSurveyorAssistant ? 'Construction guidance and project copilot support.' : 'Construction guidance and operational navigation support.'}
                        </Typography>
                    </Box>
                </Stack>
                <IconButton onClick={() => onClose && onClose()} aria-label="Close chat">
                    <CloseIcon sx={{ color: '#f8fafc' }} />
                </IconButton>
            </Box>

            <Divider sx={{ borderColor: (theme) => alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.1 : 0.65) }} />

            <Box
                ref={scrollRef}
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    px: 2.25,
                    py: 1.75,
                    background: (theme) => theme.palette.mode === 'dark'
                        ? 'radial-gradient(circle at top, rgba(37, 99, 235, 0.14) 0%, rgba(2, 6, 23, 0) 42%), linear-gradient(180deg, rgba(2, 6, 23, 0.18) 0%, rgba(15, 23, 42, 0.08) 100%)'
                        : 'radial-gradient(circle at top, rgba(59, 130, 246, 0.1) 0%, rgba(255, 255, 255, 0) 38%), linear-gradient(180deg, rgba(248, 250, 252, 0.92) 0%, rgba(241, 245, 249, 0.88) 100%)'
                }}
            >
                <Stack spacing={1.25}>
                    {messages.map((m, idx) => {
                        const isUser = m.role === 'user';
                        return (
                            <Box key={`${idx}_${m.role}`} sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
                                <Paper
                                    sx={{
                                        p: 1.35,
                                        maxWidth: '92%',
                                        borderRadius: isUser ? '22px 22px 8px 22px' : '22px 22px 22px 8px',
                                        bgcolor: isUser ? 'transparent' : (theme) => alpha(theme.palette.background.paper, 0.9),
                                        background: isUser
                                            ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 52%, #0f766e 100%)'
                                            : (theme) => theme.palette.mode === 'dark'
                                                ? 'linear-gradient(180deg, rgba(30, 41, 59, 0.96) 0%, rgba(15, 23, 42, 0.9) 100%)'
                                                : 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.94) 100%)',
                                        color: isUser ? '#eff6ff' : 'text.primary',
                                        border: '1px solid',
                                        borderColor: isUser ? alpha('#60a5fa', 0.26) : 'divider',
                                        boxShadow: isUser
                                            ? '0 16px 30px rgba(37, 99, 235, 0.22)'
                                            : '0 12px 24px rgba(15, 23, 42, 0.08)'
                                    }}
                                >
                                    <Typography variant="caption" sx={{ fontWeight: 900, color: isUser ? 'rgba(239, 246, 255, 0.78)' : 'text.secondary' }}>
                                        {isUser ? 'You' : 'Assistant'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.content}</Typography>

                                    {!isUser && m?.nav && m.nav.view ? (
                                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => {
                                                    try {
                                                        if (onNavigate) onNavigate(m.nav);
                                                    } catch {
                                                        // ignore
                                                    }
                                                }}
                                                sx={{
                                                    mt: 0.25,
                                                    borderRadius: 999,
                                                    fontWeight: 800,
                                                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 52%, #0f766e 100%)',
                                                    boxShadow: '0 12px 24px rgba(37, 99, 235, 0.22)'
                                                }}
                                            >
                                                {String(m?.nav?.label || 'Open')}
                                            </Button>
                                        </Box>
                                    ) : null}
                                </Paper>
                            </Box>
                        );
                    })}

                    {error ? (
                        <Typography variant="body2" sx={{ color: '#b91c1c', fontWeight: 800, px: 0.5 }}>{error}</Typography>
                    ) : null}
                </Stack>
            </Box>

            <Divider sx={{ borderColor: (theme) => alpha(theme.palette.divider, 0.8) }} />

            <Box
                sx={{
                    px: 2.25,
                    py: 1.5,
                    background: (theme) => theme.palette.mode === 'dark'
                        ? alpha('#020617', 0.16)
                        : alpha('#ffffff', 0.82)
                }}
            >
                <Stack spacing={1}>
                    <Box
                        component="textarea"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder={canUseSurveyorAssistant ? 'Ask a construction question, or ask about the project…' : 'Ask a construction question…'}
                        rows={2}
                        style={{
                            width: '100%',
                            resize: 'none',
                            padding: '14px 16px',
                            borderRadius: 16,
                            border: '1px solid rgba(148, 163, 184, 0.3)',
                            outline: 'none',
                            fontFamily: 'inherit',
                            fontSize: 14,
                            lineHeight: 1.5,
                            background: 'rgba(255,255,255,0.92)',
                            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)'
                        }}
                        disabled={busy}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                send();
                            }
                        }}
                    />

                    <Stack direction="row" spacing={1} justifyContent="space-between">
                        <Button
                            variant="text"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={() => setMessages([{ role: 'assistant', content: `Hi ${firstNameFromDisplayName(displayName)}, what can I help you with today?` }])}
                            disabled={busy}
                            sx={{ borderRadius: 999, fontWeight: 800 }}
                        >
                            Clear
                        </Button>
                        <Button
                            variant="contained"
                            endIcon={<SendIcon />}
                            onClick={send}
                            disabled={busy || !String(input || '').trim()}
                            sx={{
                                borderRadius: 999,
                                px: 2,
                                fontWeight: 800,
                                color: '#ffffff !important',
                                WebkitTextFillColor: '#ffffff',
                                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 52%, #0f766e 100%)',
                                boxShadow: '0 16px 28px rgba(37, 99, 235, 0.24)',
                                '& .MuiButton-label': {
                                    color: '#ffffff !important',
                                    WebkitTextFillColor: '#ffffff'
                                },
                                '& .MuiButton-endIcon svg': {
                                    color: '#ffffff !important',
                                    fill: '#ffffff'
                                },
                                '& .MuiButton-root, & .MuiButtonBase-root, & span': {
                                    color: '#ffffff !important',
                                    WebkitTextFillColor: '#ffffff'
                                },
                                '& .MuiButton-endIcon': {
                                    color: '#ffffff !important'
                                },
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 52%, #0f766e 100%)'
                                }
                            }}
                        >
                            {busy ? 'Thinking…' : 'Send'}
                        </Button>
                    </Stack>

                </Stack>
            </Box>
        </Stack>
    );

    if (anchorEl) {
        return (
            <Popper
                open={Boolean(open)}
                anchorEl={anchorEl}
                placement="right-start"
                modifiers={[{ name: 'offset', options: { offset: [0, 12] } }]}
                sx={{ zIndex: (t) => t.zIndex.modal + 1 }}
            >
                <ClickAwayListener onClickAway={() => onClose && onClose()}>
                    <Paper
                        sx={{
                            width: 420,
                            height: 560,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            borderRadius: 5,
                            border: '1px solid',
                            borderColor: (theme) => alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.12 : 0.7),
                            background: (theme) => theme.palette.mode === 'dark'
                                ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(15, 23, 42, 0.9) 100%)'
                                : 'linear-gradient(180deg, rgba(255, 255, 255, 0.97) 0%, rgba(241, 245, 249, 0.98) 100%)',
                            boxShadow: (theme) => theme.palette.mode === 'dark'
                                ? '0 28px 90px rgba(2, 6, 23, 0.54)'
                                : '0 28px 72px rgba(15, 23, 42, 0.22)',
                            backdropFilter: 'blur(18px)',
                            WebkitBackdropFilter: 'blur(18px)'
                        }}
                    >
                        {content}
                    </Paper>
                </ClickAwayListener>
            </Popper>
        );
    }

    return (
        <Drawer
            anchor="right"
            open={Boolean(open)}
            onClose={() => onClose && onClose()}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 420 },
                    overflow: 'hidden',
                    borderLeft: '1px solid',
                    borderColor: (theme) => alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.12 : 0.7),
                    background: (theme) => theme.palette.mode === 'dark'
                        ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(15, 23, 42, 0.9) 100%)'
                        : 'linear-gradient(180deg, rgba(255, 255, 255, 0.97) 0%, rgba(241, 245, 249, 0.98) 100%)',
                    boxShadow: (theme) => theme.palette.mode === 'dark'
                        ? '0 28px 90px rgba(2, 6, 23, 0.54)'
                        : '0 28px 72px rgba(15, 23, 42, 0.22)'
                }
            }}
        >
            {content}
        </Drawer>
    );
}
